import { Navigate, useLocation, useParams } from 'react-router';
import type { ReactNode } from 'react';
import { Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import type { ArrangementDto } from '@pams/shared';
import {
  mayAuthorArrangement,
  mayAuthorPolicy,
  mayEditIssuedArrangement,
  mayReadDutyAnalytics,
  mayUseTemplates,
  meetsRequirement,
  type CapabilityRequirement,
  actingOrgUnitTypeOf,
} from '@/lib/accessRole';
import { useArrangementAuthoringSetting } from '@/hooks/useArrangementAuthoringSetting';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/**
 * Route guards.
 *
 * ⚠️ Neither of these is a security control. The API rejects an unauthorised call with
 * a 403, and an out-of-scope read with a 404 from the query itself. These exist so the
 * app does not render a page that is guaranteed to fail.
 */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { status } = useAuth();
  const location = useLocation();

  // `bootstrapping` never reaches here — AuthProvider gates the whole tree until it
  // resolves — but the branch is kept so this component is correct in isolation.
  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
};

/**
 * ⚠️ [amended C5b §5] A `CapabilityRequirement`, asked of the OFFICER through `meetsRequirement` —
 * so `/usage-analytics` (`ANALYTICS_ACCESS`) and [amended AUDIT-FLAG] `/audit-log` (`AUDIT_ACCESS`)
 * both bounce a super admin who has not been granted the one `access_analytics` flag, exactly as the
 * API refuses them. [amended LOCATION-INTEL] So does `/location-intel` (`LOCATION_INTEL_ACCESS`), for any
 * dashboard role not granted `location_intel`. Every other route passes a bare
 * capability, which answers what `can()` answered.
 */
export const RequireCapability = ({
  capability,
  children,
}: {
  capability: CapabilityRequirement;
  children: ReactNode;
}) => {
  const { officer } = useAuth();
  if (!officer || !meetsRequirement(officer, capability)) return <Navigate to="/forbidden" replace />;
  return <>{children}</>;
};

/**
 * ⚠️ [C5c §3] A POLICY IS AUTHORED AT THE COMMISSIONERATE — THE ROUTE'S HALF OF THE SERVER'S SEAT GUARD.
 *
 * `policy.write` lists NODAL_ADMIN now, so `RequireCapability` alone would open the create form to a
 * divisional nodal admin whose every save `assertMayAuthorPolicy` refuses — the reachable form that
 * cannot save, which [C3c §9c] closed once already. `/policies/new` therefore takes both, nested:
 * the capability, then `mayAuthorPolicy`, the same predicate the nav row and the page read.
 *
 * ⚠️ SYNCHRONOUS, UNLIKE `RequireDivision`. Both inputs — the role and the org unit type — arrive on
 * `/auth/me`, which `AuthProvider` resolves before any route renders, so there is nothing to wait
 * for and no `<Navigate replace>` that a later answer would need to undo.
 *
 * ⚠️ AND IT IS COURTESY, like everything in this file: the service refuses on POSITION whatever this
 * renders, and this reads the `type` label (`mayAuthorPolicy`'s header says why that is safe here).
 */
export const RequirePolicyAuthor = ({ children }: { children: ReactNode }) => {
  const { officer } = useAuth();
  if (!officer || !mayAuthorPolicy(officer.accessRole, actingOrgUnitTypeOf(officer))) {
    return <Navigate to="/forbidden" replace />;
  }
  return <>{children}</>;
};

/**
 * ⚠️ [P7B §4] THE TEMPLATE PAGES ARE THE COMMISSIONERATE'S — THE ROUTE'S HALF OF `assertMayRunTemplates`.
 *
 * `RequirePolicyAuthor`'s shape exactly: `arrangement.write` lists NODAL_ADMIN, and only one seated (or
 * acting) at the commissionerate may use a template set, so every `/templates…` route nests this inside
 * `RequireCapability('arrangement.write')`. A divisional nodal admin who types the path is bounced here
 * rather than reaching pages whose every request the API refuses.
 *
 * ⚠️ SYNCHRONOUS — both inputs arrive on `/auth/me`, which `AuthProvider` resolves before any route
 * renders, so there is nothing to wait for and no `<Navigate replace>` a later answer would need to undo.
 * And courtesy, like everything in this file: the API refuses on POSITION whatever this renders.
 *
 * ⚠️ NO FLAG. The template pages are not a `master_superadmin` override; only the two overrides are.
 */
export const RequireTemplateAuthor = ({ children }: { children: ReactNode }) => {
  const { officer } = useAuth();
  if (!mayUseTemplates(officer)) return <Navigate to="/forbidden" replace />;
  return <>{children}</>;
};

/**
 * ⚠️ [P7C §2] DUTY ANALYTICS IS READ AT THE COMMISSIONERATE AND AT A DIVISION — THE ROUTE'S HALF OF
 * THE SERVER'S LEVEL GATE.
 *
 * `dashboard.access` is held by every dashboard role, so `RequireCapability` alone would open the page
 * to a unit-level nodal whose one request the API refuses with `DUTY_ANALYTICS_LEVEL_REFUSAL`. Both
 * `/duty-analytics` routes therefore nest this inside `RequireCapability('dashboard.access')`, and it
 * asks `mayReadDutyAnalytics` — the same predicate the nav row reads.
 *
 * ⚠️ SYNCHRONOUS, `RequireTemplateAuthor`'s shape: the role and the acting unit arrive on `/auth/me`,
 * which `AuthProvider` resolves before any route renders. And courtesy, like everything in this file:
 * the server decides by POSITION whatever this renders, and the page prints its 403 sentence.
 */
export const RequireDutyAnalyticsReader = ({ children }: { children: ReactNode }) => {
  const { officer } = useAuth();
  if (!mayReadDutyAnalytics(officer)) return <Navigate to="/forbidden" replace />;
  return <>{children}</>;
};

/**
 * ⚠️ [C1c §7] RAISING AN ARRANGEMENT IS A DIVISION'S WORK, AND THIS CANNOT BE A CAPABILITY.
 *
 * There is no "unit nodal" role: a `NODAL_ADMIN` at a police station and one at a division hold
 * IDENTICAL capabilities, and the only thing that separates them is the node they are posted
 * at. So the predicate reads the officer's own org unit TYPE — which `/auth/me` already carries
 * on `orgUnit`, so this needed no wire change.
 *
 * ⚠️ AND IT IS THE SAME PREDICATE THE SERVER USES (`assertMayRaiseArrangement`), so a route
 * cannot be reachable behind a request that refuses it. Reaching it directly lands on
 * `/forbidden` rather than on a form whose source picker is empty and which says nothing —
 * which is exactly what a unit nodal met before C1c.
 *
 * ⚠️ [amended UX1 §8] AND THE SERVER'S PREDICATE HAS A THIRD TERM NOW — the deployment's
 * `arrangement.nodalAuthor` setting, DEFAULT `false`. Both sides read it; see below for why
 * this gate had to learn to wait.
 */
export const RequireDivision = ({ children }: { children: ReactNode }) => {
  const { officer } = useAuth();
  const { nodalsMayAuthor, isPending } = useArrangementAuthoringSetting();

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [UX1 §8] IT WAITS NOW, AND THE WAIT IS THE WHOLE OF THE CLIENT HALF OF §8b.
   *
   *  Before this pass both inputs came from `/auth/me`, which `AuthProvider` has already
   *  resolved before any route renders — so deciding synchronously was correct. One input now
   *  arrives over the network, and `<Navigate replace>` IS NOT UNDOABLE BY A LATER ANSWER: it
   *  has already replaced the history entry. Deciding early would bounce every division nodal
   *  to `/forbidden` and leave them there.
   *
   *  ⚠️ IT IS STILL NOT A SECURITY CONTROL — see this file's header. `assertMayRaiseArrangement`
   *  refuses the create on the server whatever this renders, which is §8b's actual requirement;
   *  this exists so the app does not open a form every request behind it would refuse.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  if (isPending) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" data-testid="route-gate-waiting">
        <Spin />
      </div>
    );
  }
  if (!officer || !mayAuthorArrangement(officer.accessRole, actingOrgUnitTypeOf(officer), nodalsMayAuthor)) {
    return <Navigate to="/forbidden" replace />;
  }
  return <>{children}</>;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §2b] AN ISSUED ORDER IS EDITED BY THE OFFICER WHO RAISED IT, OR A SUPER ADMIN.
 *
 *  §2b's own rule is that a reachable form which cannot save is worse than a refused route —
 *  [C3c §9c] had to close exactly that shape once already, when a nodal reached the edit URL
 *  and typed into a form whose every save the server refused. So the ownership rule is refused
 *  on the server (`assertMayEditIssuedArrangement`) AND the door is not opened here.
 *
 *  ⚠️ IT COSTS NO EXTRA REQUEST. The key is `queryKeys.arrangements.detail(id)` — byte-identical
 *  to the one `ArrangementBuilder` reads for `existing`, so the gate and the form share one cache
 *  entry and whichever mounts first fills it. A second key would fetch the order twice.
 *
 *  ⚠️ IT WAITS, FOR `RequireDivision`'s MEASURED REASON. `<Navigate replace>` is NOT UNDOABLE by
 *  a later answer — it has already replaced the history entry — so deciding before the record
 *  lands would bounce the creator to `/forbidden` and leave them there.
 *
 *  ⚠️ A FAILED READ RENDERS THE CHILDREN RATHER THAN REFUSING. A 404 here means the order is
 *  outside the caller's scope and the builder's own read will answer the same way, with a
 *  message; bouncing to `/forbidden` on a network blip would claim a refusal the API never made.
 *  The SERVER is the control, and it has the record.
 *
 *  ⚠️ AND IT IS NOT A CAPABILITY. There is no key that could express "raised this one" — it is a
 *  fact about a row, which is why `assertMayEditIssuedArrangement` is a second function rather
 *  than a clause inside `assertMayAuthorArrangement`. §10's rule that a capability always
 *  granted alongside another grants nothing cuts the same way: every holder of
 *  `arrangement.write` would hold it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const RequireArrangementOwner = ({ children }: { children: ReactNode }) => {
  const { officer } = useAuth();
  const { arrangementId = '' } = useParams();

  const existing = useQuery({
    queryKey: queryKeys.arrangements.detail(arrangementId),
    queryFn: ({ signal }) => api.get<ArrangementDto>(`/arrangements/${arrangementId}`, { signal }),
    enabled: arrangementId.length > 0,
  });

  if (existing.isPending && arrangementId.length > 0) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" data-testid="route-gate-waiting">
        <Spin />
      </div>
    );
  }

  /*
   * ⚠️ THE PREDICATE IS `lib/accessRole.ts`'s, NEVER A ROLE COMPARISON HERE. That file holds
   * every offer gate in the app and keeps §10's no-role-ladder property; this component is the
   * routing half and owns only the WAIT.
   */
  if (!officer) return <>{children}</>;
  if (mayEditIssuedArrangement(officer.accessRole, officer.id, existing.data)) return <>{children}</>;
  return <Navigate to="/forbidden" replace />;
};
